import { AuthPanel } from '@ccp/ui';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { AcceptInvitationForm } from '../../features/auth/components/accept-invitation-form.js';
import { currentOrRestoredStatus } from '../../session/session.js';

export const Route = createFileRoute('/auth/accept')({
  async beforeLoad() {
    // Somebody already signed in is not accepting an invitation: they have an account. The cookie
    // may well still be good from another tab, which is why this asks rather than assuming.
    if ((await currentOrRestoredStatus()) === 'authenticated') {
      throw redirect({ to: '/' });
    }
  },
  component: Accept,
});

/**
 * The page a tenant's first user arrives on.
 *
 * It has to work before anybody in this tenant has a session, because accepting an invitation is
 * how the first one comes to exist: provisioning writes an invitation and emails a code, and
 * nothing else creates an account. Until this page existed the only way in was an API call by
 * hand — which is what the owner of this deployment's first tenant actually had to do, on a
 * deployment where every other step was a page (M4-13).
 *
 * No search parameters: the code is pasted into the form rather than carried in the URL, so a
 * bearer credential never reaches the browser's history, a `Referer`, or an access log.
 */
function Accept() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();

  return (
    <AuthPanel title={t('accept.title')} description={t('accept.description')}>
      {/* Straight into the workspace: accepting sets a password and signs in with it, so there is
          no reason to show a sign-in form to somebody who just proved who they are. */}
      <AcceptInvitationForm onAccepted={() => void navigate({ to: '/' })} />
    </AuthPanel>
  );
}
